import { ChevronLeft, ChevronRight } from 'lucide-react';
import React from 'react';
import type { PaginationMeta } from '../../types/pagination';

interface PaginationControlsProps {
	pagination: PaginationMeta;
	onPageChange: (page: number) => void;
	onNextPage: () => void;
	onPreviousPage: () => void;
	loading?: boolean;
	className?: string;
	pageSizeOptions?: number[];
	onPageSizeChange?: (pageSize: number) => void;
	showPageSizeSelector?: boolean;
}

export const PaginationControls = React.memo(function PaginationControls({
	pagination,
	onPageChange,
	onNextPage,
	onPreviousPage,
	loading = false,
	className = '',
	pageSizeOptions = [10, 20, 50, 100],
	onPageSizeChange,
	showPageSizeSelector = true,
}: PaginationControlsProps) {
	const { page, pages, total, limit, hasPrevious, hasNext } = pagination;

	// Generate page numbers to show
	const getPageNumbers = () => {
		const delta = 2; // Pages to show on each side of current page
		const range = [];
		const rangeWithDots = [];

		// Calculate range around current page
		for (let i = Math.max(2, page - delta); i <= Math.min(pages - 1, page + delta); i++) {
			range.push(i);
		}

		// Add first page
		if (page - delta > 2) {
			rangeWithDots.push(1, '...');
		} else {
			rangeWithDots.push(1);
		}

		// Add middle range
		rangeWithDots.push(...range);

		// Add last page
		if (page + delta < pages - 1) {
			rangeWithDots.push('...', pages);
		} else if (pages > 1) {
			rangeWithDots.push(pages);
		}

		return rangeWithDots;
	};

	// Calculate current showing range
	const startItem = (page - 1) * limit + 1;
	const endItem = Math.min(page * limit, total);

	return (
		<div
			className={`flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 ${className}`}
		>
			{/* Left side: Results summary and page size selector */}
			<div className="flex flex-col sm:flex-row items-center gap-4">
				{/* Results summary */}
				<div className="text-sm text-gray-700 dark:text-gray-300">
					{total > 0 ? (
						<>
							Showing <span className="font-medium">{startItem}</span> to{' '}
							<span className="font-medium">{endItem}</span> of{' '}
							<span className="font-medium">{total}</span> results
						</>
					) : (
						'No results found'
					)}
				</div>

				{/* Page size selector */}
				{showPageSizeSelector && onPageSizeChange && total > 0 && (
					<div className="flex items-center gap-2">
						<label
							htmlFor="page-size"
							className="text-sm text-gray-700 dark:text-gray-300"
						>
							Show:
						</label>
						<select
							id="page-size"
							value={limit}
							onChange={e => onPageSizeChange(Number(e.target.value))}
							disabled={loading}
							className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{pageSizeOptions.map(size => (
								<option key={size} value={size}>
									{size}
								</option>
							))}
						</select>
						<span className="text-sm text-gray-700 dark:text-gray-300">per page</span>
					</div>
				)}
			</div>

			{/* Page controls - Always show if we have data */}
			{total > 0 && (
				<div className="flex items-center gap-2">
					{/* Previous button */}
					<button
						onClick={onPreviousPage}
						disabled={!hasPrevious || loading}
						className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors"
					>
						<ChevronLeft className="w-4 h-4" />
						Previous
					</button>

					{/* Page numbers */}
					<div className="flex items-center gap-1">
						{pages <= 7
							? // Show all pages if <= 7 pages
							  Array.from({ length: pages }, (_, i) => i + 1).map(pageNum => (
									<button
										key={pageNum}
										onClick={() => onPageChange(pageNum)}
										disabled={loading}
										className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
											pageNum === page
												? 'bg-blue-600 text-white border border-blue-600'
												: 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
										} disabled:opacity-50 disabled:cursor-not-allowed`}
									>
										{pageNum}
									</button>
							  ))
							: // Show pages with dots for > 7 pages
							  getPageNumbers().map((pageNum, index) =>
									pageNum === '...' ? (
										<span
											key={`dots-${index}`}
											className="px-2 py-2 text-gray-400 dark:text-gray-500"
										>
											...
										</span>
									) : (
										<button
											key={pageNum}
											onClick={() => onPageChange(pageNum as number)}
											disabled={loading}
											className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
												pageNum === page
													? 'bg-blue-600 text-white border border-blue-600'
													: 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
											} disabled:opacity-50 disabled:cursor-not-allowed`}
										>
											{pageNum}
										</button>
									)
							  )}
					</div>

					{/* Next button */}
					<button
						onClick={onNextPage}
						disabled={!hasNext || loading}
						className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors"
					>
						Next
						<ChevronRight className="w-4 h-4" />
					</button>
				</div>
			)}

			{/* Loading indicator for page changes */}
			{loading && (
				<div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm flex items-center justify-center">
					<div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
						<div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
						<span className="text-sm text-gray-600 dark:text-gray-400">Loading...</span>
					</div>
				</div>
			)}
		</div>
	);
});
